import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { ForgotPasswordInput } from "../../api/forgot-password";
import { forgotPasswordSchemaInput, requestNewPassword } from "../../api/forgot-password";
import DocumentTextField from "../../components/DocumentTextField";
import { obfuscateEmail } from "../../utils/obfuscateEmail";
import { removeNotNumbers } from "../../utils/removeNotNumbers";
import { notify } from "../../pages/public/components/Toast";

function ForgotPassword(): React.JSX.Element {
  const { t } = useTranslation();
  const [stateData, setStateData] = React.useState<"success" | "error" | undefined>(undefined);
  const [registeredEmail, setRegisteredEmail] = React.useState<string | undefined>(undefined);

  const methods = useForm<ForgotPasswordInput>({
    defaultValues: { cpf: "" },
    resolver: yupResolver(forgotPasswordSchemaInput),
    criteriaMode: "all",
    mode: "all",
  });

  const { control, handleSubmit, formState } = methods;

  const mutation = useMutation({
    mutationFn: async (data: ForgotPasswordInput) => {
      const payload = { ...data, cpf: removeNotNumbers(String(data.cpf)) };
      return await requestNewPassword(payload);
    },
    onSuccess: ({ data }) => {
      setRegisteredEmail(data?.email || "");
      setStateData("success");
    },
    onError: (error: any) => {
      const code = (error?.response?.data as any)?.code;
      notify("", "error", code);
      setStateData("error");
    },
  });

  const onSubmit: SubmitHandler<ForgotPasswordInput> = (data): void => {
    mutation.mutate(data);
  };

  if (stateData === "success")
    return (
      <section>
        <Stack gap={3}>
          <Typography color="success.main" variant="h4">
            {t("forgotPassword.successTitle")}
          </Typography>
          <Typography>{t("forgotPassword.successMessage")}</Typography>
          {registeredEmail ? (
            <TextField
              disabled
              error={false}
              fullWidth
              helperText=""
              label={t("forgotPassword.registeredEmailLabel")}
              sx={{
                "& .MuiFormLabel-root": {
                  color: "primary.main",
                },
              }}
              value={obfuscateEmail(registeredEmail)}
              variant="outlined"
            />
          ) : null}
          <Typography>{t("forgotPassword.accessEmail")}</Typography>
        </Stack>
      </section>
    );

  return (
    <section>
      <FormProvider {...methods}>
        <form autoComplete="off" autoSave="off" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <Stack alignItems="center" gap={3}>
            <Typography fontWeight="500" textAlign="left" variant="h4" width="100%">
              {t("forgotPassword.title")}
            </Typography>
            <Controller
              control={control}
              name="cpf"
              render={({ field, fieldState: { error } }) => (
                <DocumentTextField
                  {...field}
                  error={Boolean(error)}
                  fullWidth
                  helperText={error ? error.message : t("common.numbersOnly")}
                  name="cpf"
                  variant="outlined"
                />
              )}
            />
            <Box style={{ width: "100%" }} textAlign="center">
              <Button disabled={!formState.isValid} fullWidth size="large" type="submit" variant="contained">
                {t("forgotPassword.submitButton")}
              </Button>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </section>
  );
}

export const Route = createFileRoute("/__public/forgot-password")({
  component: ForgotPassword,
});